/**
 * COM-9.12 — the contract-storage sweeper, made visible.
 *
 * The sweeper and its run table both worked from the day they were written with
 * NOTHING CALLING THEM. A sweeper with no scheduler is the exact AD-33 defect the
 * design guards against, and three scheduled jobs in this codebase have already
 * died of it quietly. The schedule is now `contract-storage-sweep-daily` in
 * `cron.job`; this card is how anybody finds out when it stops.
 *
 * ⚠️ SUPERADMIN ONLY, AND THE DATABASE AGREES. The contracts bucket spans every
 * account, so this is not a tenant's fact to read.
 * `contract_storage_sweep_health()` refuses a non-superadmin rather than answering
 * an empty object, so this card cannot be the security boundary and is not
 * pretending to be one.
 *
 * ⚠️ THE STATE WORTH RENDERING IS `reporting_only`. A cron that forgets
 * `dry_run=false` records a clean pass every single day and reaps nothing, which
 * every "is it running" check calls healthy.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { usePermissions } from '@/contexts/PermissionContext';
import {
  CONTRACT_SWEEP_COPY as COPY,
  contractSweepIsHealthy,
  contractSweepVerdict,
  type ContractSweepHealth,
} from '@/lib/contractStorageSweep';
import { fetchContractSweepHealth } from '@/services/contractStorageSweepService';

export function ContractStorageSweepCard() {
  const { hasPermission, loading: permsLoading } = usePermissions();
  const isSuperadmin = hasPermission('system.override');

  // ⚠️ `null` is "we did not read it", never "all clear".
  const [health, setHealth] = useState<ContractSweepHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setHealth(await fetchContractSweepHealth());
    } catch (e) {
      // Everything stays null, which renders as unknown rather than healthy.
      setHealth(null);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (permsLoading || !isSuperadmin) return;
    void load();
  }, [permsLoading, isSuperadmin, load]);

  if (permsLoading || !isSuperadmin) return null;

  const verdict = health === null ? null : contractSweepVerdict(health);
  const healthy = verdict === null ? null : contractSweepIsHealthy(verdict.state);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {healthy === true && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
          {healthy === false && <AlertTriangle className="h-4 w-4 text-destructive" />}
          {COPY.title}
        </CardTitle>
        <CardDescription>{COPY.description}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-3">
        {error !== null && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Reading sweeper health&hellip;
          </div>
        ) : (
          verdict !== null && (
            <>
              <p className={healthy ? 'text-sm font-medium' : 'text-sm font-medium text-destructive'}>
                {verdict.label}
              </p>
              <p className="text-sm text-muted-foreground">{verdict.detail}</p>

              <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
                <div>
                  <dt className="text-muted-foreground">Objects examined</dt>
                  <dd className="font-semibold tabular-nums">{health?.last_examined ?? 0}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Removed last pass</dt>
                  <dd className="font-semibold tabular-nums">{health?.last_deleted ?? 0}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Removed in total</dt>
                  <dd className="font-semibold tabular-nums">{health?.total_deleted ?? 0}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Passes in 24h</dt>
                  <dd className="font-semibold tabular-nums">{health?.runs_last_24h ?? 0}</dd>
                </div>
              </dl>

              <p className="text-xs text-muted-foreground">{COPY.scope}</p>
            </>
          )
        )}
      </CardContent>
    </Card>
  );
}
